import { computed, inject, Injectable, Signal } from '@angular/core';
import { Params, QueryParamsHandling } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { map, switchMap } from 'rxjs/operators';
import { AppRoutesService, CumLaudeRouteData, getUrl, RouteDef } from './routes.service';
import { CurrentUrlService, stripQueryParams } from '@cumlaude/shared-services';

export interface TopLevelSubMenuItem {
	name: string;
	routeElements: RouteDef<any>[];
	queryParams?: Params;
	queryParamsHandling?: QueryParamsHandling;
}

export interface TopLevelMenuItem {
	name: string;
	routeElements: RouteDef<any>[];
	queryParams?: Params;
	queryParamsHandling?: QueryParamsHandling;
	subMenus?: TopLevelSubMenuItem[];
}

export interface TabMenuItem {
	name: string;
	fullName: string;
	routeElements: RouteDef<any>[];
	queryParams?: Params;
	queryParamsHandling?: QueryParamsHandling;
}

export interface TopLevelMenu {
	name: string;
	menus: TopLevelMenuItem[];
}

export interface TabMenu {
	menus: TabMenuItem[];
}

const CHAR_WIDTH_PX = 8;

const MENU_ITEM_PADDING_PX = 24;

export const REM_IN_PX = 16;

const LOGO_SIZE_REM = 10;

@Injectable({
	providedIn: 'root',
})
export abstract class AppMenuService<T extends CumLaudeRouteData> {
	breakpointObserver = inject(BreakpointObserver);
	readonly routesService = inject(AppRoutesService);
	readonly currentUrlService = inject(CurrentUrlService);

	readonly topLevelMenus = computed(() => this.getTopLevelMenus(this.routesService.activeAuthorisedRoutes()));

	readonly topLevelMenu = computed(() => {
		const currentPath = this.currentUrlService.currentPath();
		if (!currentPath) return <TopLevelMenuItem[]>[];

		const topLevelMenuItems = this.topLevelMenus().find((topLevelMenu) =>
			this.matchesTopLevelMenu(topLevelMenu, currentPath.substring(1))
		)?.menus;
		return topLevelMenuItems ?? <TopLevelMenuItem[]>[];
	});

	readonly tabMenus = computed(() => this.getTabMenus(this.routesService.activeAuthorisedRoutes()));

	readonly tabMenu = computed(() => {
		const currentPath = this.currentUrlService.currentPath();
		if (!currentPath) return <TabMenuItem[]>[];

		const tabMenuItems = this.tabMenus().find((tabMenu) => this.matchesTabMenu(tabMenu, currentPath.substring(1)))?.menus;
		return tabMenuItems ?? <TabMenuItem[]>[];
	});

	abstract rightTopLevelMenuItemSize: Signal<number>;

	readonly topLevelMenuSize = computed(() => {
		const topLevelMenuItems = this.topLevelMenu();
		let size = 0;
		for (const item of topLevelMenuItems) {
			size += item.name.length * CHAR_WIDTH_PX + MENU_ITEM_PADDING_PX;
		}
		return size;
	});

	private readonly logoSize = LOGO_SIZE_REM * REM_IN_PX;

	readonly requiredWindowSize = computed(() => {
		return this.topLevelMenuSize() + this.rightTopLevelMenuItemSize() + this.logoSize;
	});

	readonly showTopLevelMenu = toSignal(
		toObservable(this.requiredWindowSize).pipe(
			switchMap((size) => this.breakpointObserver.observe(`(min-width: ${size}px)`)),
			map((state) => state.matches)
		),
		{ initialValue: true }
	);

	readonly showHamburgerMenu = computed(() => !this.showTopLevelMenu());

	abstract matchTopLevelMenuQueryParams(route: RouteDef<T>): Params;

	abstract matchTabMenuQueryParams(route: RouteDef<T>): Params;

	abstract isTopLevelMenu(routeData: T): boolean;

	abstract getName(routeData: T): string;

	private getTopLevelMenus(authorisedRoutes: RouteDef<T>[]): TopLevelMenu[] {
		const topLevelMenus: TopLevelMenu[] = [];
		for (const route of authorisedRoutes) {
			const routeData = route.routeData!;
			let topLevelMenu = topLevelMenus.find((topLevelMenu) => topLevelMenu.name === routeData.base);
			if (!topLevelMenu) {
				topLevelMenu = {
					name: routeData.base!,
					menus: [],
				};
				topLevelMenus.push(topLevelMenu);
			}
			const topLevelSubMenus = this.getTopLevelSubMenus(route);

			let queryParams;
			if (topLevelSubMenus) queryParams = topLevelSubMenus[0].queryParams;
			else {
				const subRoutes = route.subRoutes;
				queryParams = subRoutes ? this.matchTopLevelMenuQueryParams(subRoutes[0]) : undefined;
			}

			topLevelMenu.menus.push({
				name: this.getName(routeData),
				routeElements: route.elements!,
				queryParams: queryParams,
				queryParamsHandling: queryParams ? 'merge' : undefined,
				subMenus: topLevelSubMenus,
			});
		}
		return topLevelMenus;
	}

	private getTopLevelSubMenus(route: RouteDef<T>): TopLevelSubMenuItem[] | undefined {
		const subRoutes = route.subRoutes;
		if (!subRoutes) return;

		const topLevelSubMenus = subRoutes
			.filter((subRoute) => this.isTopLevelMenu(subRoute.routeData!))
			.map((subRoute) => {
				const subSubRoutes = subRoute.subRoutes;
				const queryParams = subSubRoutes ? this.matchTopLevelMenuQueryParams(subSubRoutes[0]) : undefined;
				return {
					name: this.getName(subRoute.routeData!),
					routeElements: subRoute.elements!,
					queryParams: queryParams,
					queryParamsHandling: queryParams ? <QueryParamsHandling>'merge' : undefined,
				};
			});

		return topLevelSubMenus.length > 0 ? topLevelSubMenus : undefined;
	}

	private getTabMenus(authorisedRoutes: RouteDef<T>[]): TabMenu[] {
		const tabMenus: TabMenu[] = [];
		for (const route of authorisedRoutes) {
			const subRoutes = route.subRoutes;
			if (subRoutes) {
				const subTop = subRoutes.some((subRoute) => this.isTopLevelMenu(subRoute.routeData!));
				if (subTop) {
					tabMenus.push(...this.getTabMenus(subRoutes));
				} else {
					tabMenus.push({
						menus: this.getTabSubMenus(subRoutes),
					});
				}
			}
		}
		return tabMenus;
	}

	private getTabSubMenus(subRoutes: RouteDef<T>[]): TabMenuItem[] {
		return subRoutes.map((subRoute) => {
			const redirectTarget = subRoute.subRoutes?.[0] ?? subRoute;
			const queryParams = this.matchTabMenuQueryParams(redirectTarget);
			return {
				name: this.getName(subRoute.routeData!),
				fullName: subRoute.routeData!.name!,
				routeElements: subRoute.elements!,
				queryParams: queryParams,
				queryParamsHandling: queryParams ? <QueryParamsHandling>'merge' : undefined,
			};
		});
	}

	private matchesTopLevelMenu(topLevelMenu: TopLevelMenu, currentUrl: string): boolean {
		const matchingUrl = stripQueryParams(currentUrl).split('/')[0];
		return topLevelMenu.menus.some((menu) => menu.routeElements[0].path === matchingUrl);
	}

	private matchesTabMenu(tabMenu: TabMenu, currentUrl: string): boolean {
		const matchingUrl = stripQueryParams(currentUrl);
		return tabMenu.menus.some((menu) => {
			const url = getUrl(menu).substring(1);
			return url === matchingUrl || matchingUrl.startsWith(url);
		});
	}
}
